import { useState, useMemo } from 'react';
import { Editor, Element as SlateElement, Node, Transforms } from 'slate';
import { CustomElement } from '../utils/element-renderer';
import { STOP_MESSAGE_PRESETS } from '../toolbar/stop-message-dialog';

interface UseStopMessageReturn {
  showStopDialog: boolean;
  setShowStopDialog: (show: boolean) => void;
  currentStopMessage: string;
  handleStopMessageChange: (message: string) => void;
}

type StopMessageElement = Extract<CustomElement, { type: 'stop-message' }>;

/**
 * Type predicate to check if a node is a StopMessageElement
 */
function isStopMessage(n: Node): n is StopMessageElement {
  return SlateElement.isElement(n) && 'type' in n && n.type === 'stop-message';
}

/**
 * Hook to manage STOP message functionality in the SMS editor.
 * Handles dialog state, message retrieval, and updates.
 */
export function useStopMessage(editor: Editor): UseStopMessageReturn {
  const [showStopDialog, setShowStopDialog] = useState(false);

  // Get current STOP message text
  const currentStopMessage = useMemo(() => {
    const stopNodes = Array.from(
      Editor.nodes<StopMessageElement>(editor, {
        at: [],
        match: isStopMessage
      })
    );
    return stopNodes.length > 0 ? stopNodes[0][0].messageText : STOP_MESSAGE_PRESETS[0].text;
  }, [editor]);

  const handleStopMessageChange = (newMessage: string) => {
    const stopNodes = Array.from(
      Editor.nodes<StopMessageElement>(editor, {
        at: [],
        match: isStopMessage
      })
    );

    if (stopNodes.length === 0) {
      // If no node exists, create one
      try {
        Transforms.insertNodes(
          editor,
          {
            type: 'stop-message',
            messageText: newMessage,
            children: [{ text: '' }]
          } as StopMessageElement,
          { at: [editor.children.length] }
        );
      } catch (_error) {
        // Silently handle errors - fallback to default state
      }
      return;
    }

    // At this point we know there's at least one stop message
    const [, path] = stopNodes[0];
    try {
      Transforms.setNodes<StopMessageElement>(
        editor,
        { messageText: newMessage },
        {
          at: path,
          match: isStopMessage
        }
      );
    } catch (_error) {
      // Silently handle errors - node will remain unchanged
    }
  };

  return {
    showStopDialog,
    setShowStopDialog,
    currentStopMessage,
    handleStopMessageChange
  };
}
